import axiosInstance from "../utils/axiosInstance";
import { EX_END_FULL_LOCATION } from "../enums/departure_location.enum";
import { parseCurrencyString } from "../utils/helpers";
import { normalizeActiveStorageUrl } from "../utils/imageUrl";
import { categoryToTourType, type GroundTourType } from "../utils/groundListingParams";

const FALLBACK_IMAGE = "/iceholidays-assets/images/china_listings_cover.png";
const FALLBACK_BRAND_LOGO = "/iceholidays-assets/images/gd-logo_round.png";
const DEFAULT_LIMIT = 8;

interface TagSummary {
  id: number;
  name: string;
  description?: string;
}

interface TaggedTourPayload {
  id: string;
  attributes: {
    itinerary_id: number;
    primary_tag: TagSummary;
    other_tags?: TagSummary[];
    itinerary: TaggedTourItineraryPayload;
  };
}

interface TaggedTourItineraryPayload {
  id: number;
  code: string;
  category: string;
  caption: string;
  country: string;
  price: string | number;
  save_amount?: string | number;
  departure_date?: string[];
  guranteed_departure_dates?: string[];
  departure_locations?: string[];
  images?: string[];
  price_currency?: string;
}

interface TaggedLandTourPayload {
  id: string;
  attributes: {
    id: number;
    code: string;
    title: string;
    category: string;
    brand: string | null;
    country: string;
    price: string | number;
    save_amount?: string | number;
    first_day_city?: string;
    inclusion?: { inc_flight?: boolean };
    travel_date_start?: string;
    travel_date_end?: string;
    min_booking?: number;
    image_url?: string;
    primary_tag?: TagSummary;
    other_tags?: TagSummary[];
  };
}

export interface SpecialOffer {
  id: number;
  image: string;
  badge: string;
  brandLogo?: string;
  brandName?: string;
  category: string;
  location: string;
  from: string;
  title: string;
  guaranteed: boolean;
  originalPrice?: string;
  save?: string;
  price: string;
  footnote?: string;
  href?: string;
  tier?: string;
  tourType?: GroundTourType;
  tourStart?: string;
  flightsIncluded?: boolean;
  minPax?: number;
}

function formatMoney(currency: string | undefined, amount: number) {
  if (currency === "MYR") return `RM ${Math.round(amount).toLocaleString("en-US")}`;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "MYR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function categoryLogo(category: string) {
  if (category === "GD FLY CRUISE") return "/iceholidays-assets/images/icons/fly_cruise.png";
  if (category?.includes("CRUISE")) return "/iceholidays-assets/images/icons/cruise.png";
  if (category === "FS") return "/iceholidays-assets/images/icons/fs.png";
  if (category === "GD Signature") return "/iceholidays-assets/images/icons/gd_signature.png";

  return FALLBACK_BRAND_LOGO;
}

function categoryBrandName(category: string) {
  if (category === "GD FLY CRUISE") return "GD FlyCruise";
  if (category?.includes("CRUISE")) return "GD World Cruises";
  if (category === "FS") return "FS Holidays";
  if (category === "GD Signature") return "The Signature Tours";
  if (category?.includes("GD In Depth") || category?.includes("GD In-Depth")) return "GD In-Depth";

  return "GD Classic";
}

function parsePrice(price?: string | number) {
  if (!price) return 0;
  if (typeof price === "number") return price;

  const value = parseCurrencyString(price);
  return Number.isFinite(value) ? value : 0;
}

class SpecialOffersApi {
  async getOffers(type: "series" | "cruise" | "ground", limit = DEFAULT_LIMIT): Promise<{ offers: SpecialOffer[]; tagIds: number[] }> {
    if (type === "ground") {
      const taggedLandToursResponse = await axiosInstance.get("/b2c2b/api/v1/tagged_land_tours", {
        params: { per: limit, homepage: true },
      });
      const taggedLandTours: TaggedLandTourPayload[] = taggedLandToursResponse.data.data || [];
      const tagIds: number[] = taggedLandToursResponse.data.meta?.tag_ids || [];
      const offers = taggedLandTours.map(({ attributes }) => this.mapGroundOffer(attributes));

      return { offers, tagIds };
    }

    const taggedToursResponse = await axiosInstance.get("/b2c2b/api/v1/tagged_tours", {
      params: { per: limit, type, homepage: true },
    });
    const taggedTours: TaggedTourPayload[] = taggedToursResponse.data.data || [];
    const tagIds: number[] = taggedToursResponse.data.meta?.tag_ids || [];
    const offers = taggedTours.map(({ attributes }) => (
      this.mapOffer(attributes.itinerary, attributes.primary_tag, type)
    ));

    return { offers, tagIds };
  }

  private mapOffer(itinerary: TaggedTourItineraryPayload, tag: TagSummary | undefined, type: "series" | "cruise"): SpecialOffer {
    const priceValue = parsePrice(itinerary.price);
    const saveAmount = Number(itinerary.save_amount || 0);
    const originalPrice = priceValue > 0 && saveAmount > 0
      ? formatMoney(itinerary.price_currency, priceValue + saveAmount)
      : undefined;
    const departureLocation = itinerary.departure_locations?.[0] || "";
    const departureLabel = EX_END_FULL_LOCATION[departureLocation] || departureLocation || "Malaysia";
    const departureDate = itinerary.departure_date?.[0];
    const query = new URLSearchParams();

    if (departureDate) query.set("depart_date", departureDate);
    if (type === "cruise") query.set("type", "cruise");

    return {
      id: itinerary.id,
      image: normalizeActiveStorageUrl(itinerary.images?.[0], FALLBACK_IMAGE),
      badge: tag?.name || "Special Offer",
      brandLogo: categoryLogo(itinerary.category),
      brandName: categoryBrandName(itinerary.category),
      category: itinerary.category,
      location: itinerary.country,
      from: departureLabel,
      title: itinerary.caption,
      guaranteed: (itinerary.departure_date || []).some((date) => itinerary.guranteed_departure_dates?.includes(date)),
      originalPrice,
      save: saveAmount > 0 ? formatMoney(itinerary.price_currency, saveAmount) : undefined,
      price: priceValue > 0 ? formatMoney(itinerary.price_currency, priceValue) : String(itinerary.price || "TBC"),
      href: `/web/itinerary/${itinerary.code}${query.toString() ? `?${query}` : ""}`,
    };
  }

  private mapGroundOffer(landTour: TaggedLandTourPayload["attributes"]): SpecialOffer {
    const priceValue = parsePrice(landTour.price);
    const saveAmount = Number(landTour.save_amount || 0);
    const hasSaving = saveAmount > 0 && priceValue > 0;

    return {
      id: landTour.id,
      image: normalizeActiveStorageUrl(landTour.image_url, FALLBACK_IMAGE),
      badge: landTour.primary_tag?.name || "Special Offer",
      category: "",
      location: "",
      from: "",
      title: landTour.title,
      guaranteed: false,
      originalPrice: hasSaving ? formatMoney("MYR", priceValue + saveAmount) : undefined,
      save: hasSaving ? formatMoney("MYR", saveAmount) : undefined,
      price: priceValue > 0 ? formatMoney("MYR", priceValue) : String(landTour.price || "TBC"),
      href: `/web/ground-tour/${landTour.code}`,
      tier: landTour.brand || undefined,
      tourType: categoryToTourType(landTour.category),
      tourStart: landTour.first_day_city,
      flightsIncluded: Boolean(landTour.inclusion?.inc_flight),
      minPax: landTour.min_booking || 1,
    };
  }
}

export default new SpecialOffersApi();
